import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const hasRun = useRef(false);  // جلوگیری از اجرای دوباره

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // اگه قبلاً اجرا شده، skip کن
      if (hasRun.current) return;
      hasRun.current = true;
      try {
        // دریافت code و state از URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        if (!code) {
          throw new Error('کد تایید یافت نشد');
        }

        setStatus('exchanging');

        // ارسال code به backend برای دریافت token و auto-login
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/oauth/basalam/exchange`,
          {
            code: code,
            redirect_uri: process.env.REACT_APP_BASALAM_REDIRECT_URI
          }
        );

        console.log('OAuth Exchange Response:', response.data);

        const { access_token, user } = response.data;

        if (!access_token) {
          throw new Error('توکن دریافت نشد');
        }

        // ذخیره token و user
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('isAuthenticated', 'true');
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        // شروع sync محصولات
        setStatus('syncing');

        try {
          // فراخوانی sync endpoint
          const syncResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/sync-products`,
            {},
            { headers: { Authorization: `Bearer ${access_token}` } }
          );

          console.log('Sync Result:', syncResponse.data);
        } catch (syncError) {
          console.error('خطا در sync محصولات:', syncError);
          // ادامه میدیم حتی اگه sync خطا داد
        }

        // نمایش success و redirect
        setStatus('success');
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1000);

      } catch (err) {
        console.error('خطا در callback:', err);
        setStatus('error');
        setError(err.response?.data?.detail || err.message || 'خطا در ورود');
      }
    };

    handleOAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-400 via-purple-300 to-blue-300">
      <div className="bg-white rounded-2xl shadow-2xl px-10 py-12 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-500 mx-auto mb-4"></div>
            <h2 className="text-xl font-bold text-gray-800">در حال پردازش...</h2>
            <p className="text-gray-500 mt-2">لطفاً صبر کنید</p>
          </>
        )}

        {status === 'exchanging' && (
          <>
            <div className="animate-pulse">
              <div className="w-16 h-16 bg-purple-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-800">احراز هویت موفق</h2>
            <p className="text-gray-500 mt-2">در حال دریافت اطلاعات...</p>
          </>
        )}

        {status === 'syncing' && (
          <>
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-500 mx-auto mb-4"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-bold text-blue-600">در حال همگام‌سازی...</h2>
            <p className="text-gray-500 mt-2">محصولات شما از باسلام دریافت می‌شود</p>
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-green-600">موفق!</h2>
            <p className="text-gray-500 mt-2">در حال هدایت به داشبورد...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-red-600">خطا!</h2>
            <p className="text-gray-600 mt-2">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition"
            >
              تلاش مجدد
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
